import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as groupApi from '@/apis/groups';

// 쿼리 키 상수화
export const GROUP_KEYS = {
  all: ['groups'],
  lists: () => [...GROUP_KEYS.all, 'list'],
  list: (params) => [...GROUP_KEYS.lists(), params],
  details: () => [...GROUP_KEYS.all, 'detail'],
  detail: (id) => [...GROUP_KEYS.details(), id],
  managers: (groupId) => [...GROUP_KEYS.all, 'managers', groupId],
};

/* ==========================================
  Queries (GET)
========================================== */

// 1. 그룹 목록 조회
export const useGroupList = (params) => {
  return useQuery({
    queryKey: GROUP_KEYS.list(params),
    queryFn: () => groupApi.fetchGroupList(params),
  });
};

// 2. 그룹 상세 조회
export const useGroupDetail = (groupId) => {
  return useQuery({
    queryKey: GROUP_KEYS.detail(groupId),
    queryFn: () => groupApi.fetchGroupDetail(groupId),
    enabled: !!groupId, // groupId가 있을 때만 실행
  });
};

// 3. 해당 그룹에 매핑된 퍼실리테이터(FT)와 교육운영자(OP) 목록 조회
export const useManagersByGroupId = (groupId) => {
  return useQuery({
    queryKey: GROUP_KEYS.managers(groupId),
    queryFn: () => groupApi.getManagersByGroupId(groupId),
    enabled: !!groupId, // groupId가 있을 때만 실행
  });
};

/* ==========================================
  Mutations (CUD)
========================================== */

// 1. 그룹 생성
export const useCreateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: groupApi.createGroup,
    onSuccess: () => {
      // 목록 갱신
      queryClient.invalidateQueries({ queryKey: GROUP_KEYS.lists() });
    },
  });
};

// 2. 그룹 수정
export const useUpdateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: groupApi.updateGroup,
    onSuccess: (data, variables) => {
      // 목록 갱신
      queryClient.invalidateQueries({ queryKey: GROUP_KEYS.lists() });
      // 상세 갱신 (수정 중인 상세 페이지가 있다면 반영)
      queryClient.invalidateQueries({ queryKey: GROUP_KEYS.detail(variables.groupId) });
    },
  });
};

// 3. 그룹 상태 변경
export const useUpdateGroupStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, groupStatus }) => groupApi.updateGroupStatus(groupId, groupStatus),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: GROUP_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: GROUP_KEYS.detail(variables.groupId) });
    },
  });
};

// 4. 그룹 삭제
export const useDeleteGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: groupApi.deleteGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GROUP_KEYS.lists() });
    },
  });
};

// 5. 그룹에 FT/OP 추가
export const useAddManagerToGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, userId }) => groupApi.addManagerToGroup(groupId, userId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: GROUP_KEYS.managers(variables.groupId) });
    },
  });
};

// 6. 그룹에서 FT/OP 제거
export const useRemoveManagerFromGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, userId }) => groupApi.removeManagerFromGroup(groupId, userId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: GROUP_KEYS.managers(variables.groupId) });
    },
  });
};